import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'apollo-react/components/Box';
import FieldActionsMenu from './FieldActionsMenu';
import QuickCommentInput from '../Comments/QuickCommentInput';
import CommentsModal from '../Comments/CommentsModal';
import FieldWavesModal from '../FieldWaves/FieldWavesModal';
import styles from './FieldActionsSection.module.css';

/**
 * FieldActionsSection - Inline actions section for form fields
 *
 * Shows an ellipsis menu icon inline after each field.
 * When clicked, shows options: Add comment, Edit field (if enabled), Field waves.
 *
 * @param {number|string} requestId - Request ID for field waves
 * @param {number|string} formId - Form ID for constructing field key
 * @param {number|string} subformId - Subform ID
 * @param {string} fieldId - Field ID from FormCrafter schema
 * @param {string} fieldLabel - Field label for context (optional)
 * @param {Array} comments - Array of comments for this field
 * @param {Function} onAddComment - Callback to add a new comment
 * @param {boolean} enableAddComment - Whether adding comments is enabled
 * @param {boolean} disabled - Whether the form is disabled (read-only)
 * @param {boolean} enableFieldEdit - Whether field editing is enabled
 * @param {boolean} enableFieldWaves - Whether field waves viewing is enabled
 * @param {Function} onEditField - Callback when edit field is clicked
 * @param {string|null} fieldKeyOverride - Optional field key override (unique rows)
 * @param {string|null} fieldWavesKeyOverride - Optional key override for field waves
 * @param {boolean} isQuickCommentOpen - Whether the quick comment input is open (controlled from parent)
 * @param {Function} onQuickCommentToggle - Callback when quick comment should open/close
 * @param {boolean} isCommentsModalOpen - Whether the comments modal is open (controlled from parent)
 * @param {Function} onCommentsModalToggle - Callback when comments modal should open/close
 * @param {Function} onCommentAdded - Callback when a comment is successfully added (for parent state sync)
 */
const FieldActionsSection = ({
  requestId,
  formId,
  subformId,
  fieldId,
  fieldLabel,
  comments = [],
  onAddComment,
  enableAddComment = false,
  disabled = false,
  enableFieldEdit = false,
  enableFieldWaves = false,
  hasWaves = false,
  onEditField,
  fieldKeyOverride = null,
  fieldWavesKeyOverride = null,
  isQuickCommentOpen = false,
  onQuickCommentToggle,
  isCommentsModalOpen = false,
  onCommentsModalToggle,
  onCommentAdded,
  requestProgress,
  requestDateEnded,
  currentUserEmail,
  onCommentUpdated,
  onCommentDeleted,
}) => {
  const { t } = useTranslation();
  const [localIsModalOpen, setLocalIsModalOpen] = useState(false);
  const [localQuickCommentOpen, setLocalQuickCommentOpen] = useState(false);
  const [isFieldWavesModalOpen, setIsFieldWavesModalOpen] = useState(false);

  const fieldKey = useMemo(() => {
    if (fieldKeyOverride) return fieldKeyOverride;
    if (formId && subformId && fieldId) {
      return `${formId}_${subformId}_${fieldId}`;
    }
    return null;
  }, [fieldKeyOverride, formId, subformId, fieldId]);

  const fieldWavesKey = useMemo(() => {
    if (fieldWavesKeyOverride) return fieldWavesKeyOverride;
    return fieldKey;
  }, [fieldWavesKeyOverride, fieldKey]);

  const isControlled = onQuickCommentToggle !== undefined;
  const quickCommentOpen = isControlled
    ? isQuickCommentOpen
    : localQuickCommentOpen;

  const isModalControlled = onCommentsModalToggle !== undefined;
  const modalOpen = isModalControlled ? isCommentsModalOpen : localIsModalOpen;

  const hasComments = comments.length > 0;

  const canShowActions = useMemo(() => {
    return (
      hasComments ||
      enableAddComment ||
      enableFieldEdit ||
      enableFieldWaves ||
      hasWaves
    );
  }, [
    hasComments,
    enableAddComment,
    enableFieldEdit,
    enableFieldWaves,
    hasWaves,
  ]);

  const handleOpenFieldWavesModal = useCallback(() => {
    setIsFieldWavesModalOpen(true);
  }, []);

  const handleCloseFieldWavesModal = useCallback(() => {
    setIsFieldWavesModalOpen(false);
  }, []);

  const handleOpenModal = useCallback(() => {
    if (isModalControlled) {
      onCommentsModalToggle(true);
      return;
    }
    setLocalIsModalOpen(true);
  }, [isModalControlled, onCommentsModalToggle]);

  const handleCloseModal = useCallback(() => {
    if (isModalControlled) {
      onCommentsModalToggle(false);
      return;
    }
    setLocalIsModalOpen(false);
  }, [isModalControlled, onCommentsModalToggle]);

  const handleAddComment = useCallback(
    async ({ comment, subFormId, fieldId, attachedFiles }) => {
      if (onAddComment) {
        return await onAddComment({
          comment,
          subFormId,
          fieldId,
          attachedFiles,
        });
      }
    },
    [onAddComment]
  );

  const modalTitle = fieldLabel
    ? t('comments_for_field', { field: fieldLabel })
    : t('comments');

  const openQuickComment = useCallback(() => {
    if (isControlled) {
      onQuickCommentToggle(true);
    } else {
      setLocalQuickCommentOpen(true);
    }
  }, [isControlled, onQuickCommentToggle]);

  const closeQuickComment = useCallback(() => {
    if (isControlled) {
      onQuickCommentToggle(false);
    } else {
      setLocalQuickCommentOpen(false);
    }
  }, [isControlled, onQuickCommentToggle]);

  const handleViewComments = useCallback(() => {
    closeQuickComment();
    handleOpenModal();
  }, [closeQuickComment, handleOpenModal]);

  const handleCommentAdded = useCallback(
    (newComment) => {
      if (onCommentAdded) {
        onCommentAdded(newComment);
      }
    },
    [onCommentAdded]
  );

  if (!canShowActions) {
    return null;
  }

  return (
    <Box className={styles.container}>
      <FieldActionsMenu
        hasComments={hasComments}
        hasWaves={hasWaves}
        enableAddComment={enableAddComment}
        enableFieldEdit={enableFieldEdit}
        disabled={disabled}
        enableFieldWaves={enableFieldWaves && !!requestId && !!fieldWavesKey}
        onAddComment={openQuickComment}
        onEditField={onEditField}
        onViewComments={handleViewComments}
        onViewFieldWaves={handleOpenFieldWavesModal}
      />

      {quickCommentOpen && enableAddComment && !disabled && !isControlled && (
        <QuickCommentInput
          subformId={subformId}
          fieldId={fieldId}
          onAddComment={handleAddComment}
          onClose={closeQuickComment}
          onCommentAdded={handleCommentAdded}
        />
      )}

      <CommentsModal
        open={modalOpen}
        onClose={handleCloseModal}
        title={modalTitle}
        comments={comments}
        enableAddComment={enableAddComment}
        disabled={disabled}
        subformId={subformId}
        fieldId={fieldId}
        onAddComment={handleAddComment}
        onCommentAdded={handleCommentAdded}
        requestProgress={requestProgress}
        requestDateEnded={requestDateEnded}
        currentUserEmail={currentUserEmail}
        requestId={requestId}
        onCommentUpdated={onCommentUpdated}
        onCommentDeleted={onCommentDeleted}
      />

      {enableFieldWaves && requestId && fieldWavesKey && (
        <FieldWavesModal
          open={isFieldWavesModalOpen}
          onClose={handleCloseFieldWavesModal}
          requestId={requestId}
          fieldKey={fieldWavesKey}
          fieldLabel={fieldLabel}
        />
      )}
    </Box>
  );
};

export default FieldActionsSection;
